/**
 * PunaJussi's breakfast: the pan on the kivetys, the cook over it, and the
 * minigame's station and reach.
 *
 * The station and the reach are hashed; the pan, the cook and what is on the
 * plate are not. See `src/config/AGENTS.md`.
 */

import { type AamupalaSpec, type MuurikkaSpec } from '../world/Level';
import { AAMUPALA } from './aamupala';

/* ------------------------------------------------------------- muurikka -- */

/**
 * ==========================================================================
 *  MUURIKKAPANNU JA KOKKI
 * ==========================================================================
 *  Breakfast, out on the kivetys in front of the navetta: a metre of steel
 *  plate on a gas ring, a man behind it with a lasta in his hand, and the
 *  smell of it going down the yard. It is the first thing the venue does for
 *  anybody who slept in a rented pirtti, and it is what the kivetys is for
 *  the rest of the year.
 *
 *  ------------------------------------------------------------------------
 *   IT IS SCENERY, AND IT IS PLACED SO THAT IT STAYS SCENERY
 *  ------------------------------------------------------------------------
 *  Not one part of this is a collider. The whole set-piece is built out of
 *  `MuurikkaSpec` - which `World` draws and nothing in `src/sim` ever reads -
 *  so it is outside `simulationFingerprint()` and adding it retires no
 *  leaderboard time. See `src/config/CLAUDE.md` for the rule and
 *  `src/world/Level.ts` for the spec.
 *
 *  That is only safe because of where it stands, so where it stands is chosen
 *  rather than eyeballed. It sits in the one clear stretch of the facade -
 *  east of the pöytäryhmä's benches, west of the lipputanko's jalusta, and
 *  eight metres east of the porstua - which is the far side of the doorway
 *  from every line that runs into it: out of the veräjä and along the wall,
 *  or down the apron from the far end of the treeline. Nobody is ever steered into
 *  this thing, and nothing about the door it stands beside has changed.
 *  `Level.test.ts` measures both of those rather than taking their word.
 *
 *  Exported for the one thing outside this file that needs to know where it
 *  stands: the mixer puts a sizzle at it (`MUURIKKA_SIHINA` in
 *  src/config/audio.ts), and `muurikka.test.ts` holds that point to this one
 *  rather than to a comment.
 * ==========================================================================
 */
export const MUURIKKA = {
  /**
   * Out on the paving between the pöytäryhmä and the lipputanko, a stride off
   * the wall. Far enough from the facade for a man to get behind it, which is
   * the whole reason a stand like this is not pushed against a wall.
   */
  x: 138.6,
  z: -28.35,
  /** Half a big one: 1.24 m of plate, which is a pan you cook for a hall on. */
  radius: 0.62,
  /** Working height. A stand this size puts the plate at a man's hip. */
  height: 0.86,
  /** Where the cook's feet are: between the plate and the wall behind it. */
  cookZ: -29.22,
  /**
   * How he stands. Square-ish to his own pan and turned a little down the
   * facade, which is the way the door is and therefore the way Kimmo comes -
   * `World` turns his head the rest of the way. See `KOKKI_LINE`.
   */
  cookYaw: -0.4,
  cookHeight: 1.82,
} as const;

export const MUURIKKAPANNU: MuurikkaSpec = {
  name: 'muurikka',
  center: [MUURIKKA.x, MUURIKKA.z],
  radius: MUURIKKA.radius,
  height: MUURIKKA.height,
  // Knobs towards the cook, because that is the side of a gas ring the knobs
  // are on. It costs nothing that the runner never sees them: the alternative
  // is a burner whose controls are out of the reach of the only man using it.
  rotationY: Math.PI,
  cook: {
    name: 'kokki',
    stand: [MUURIKKA.x - 0.05, MUURIKKA.cookZ],
    height: MUURIKKA.cookHeight,
    rotationY: MUURIKKA.cookYaw,
    // The cap says the job and the shirt says the man, which is the way round
    // a LAN wears both - see the note on `ORGA`, who is dressed by the same
    // rule indoors. It went this way round the morning breakfast stopped being
    // an offer: a lippalakki with RUOKATIIMI on it can be read across a yard
    // by somebody who has never met him, and that is now a thing a runner
    // needs to be able to do at thirty kilometres an hour.
    cap: { text: 'Ruokatiimi', color: '#141a24', background: '#e3b23c' },
    // Punamultamaalia, which is what the barn behind him is painted with, and
    // the only sponsor a yard like this has ever had.
    shirt: { text: 'PunaJussi', color: '#f6f2e7', background: '#9d2f24' },
  },
};

/**
 * ==========================================================================
 *  MÄYSÄ, JOKA SEISOO OVELLA
 * ==========================================================================
 *  The second man in the ruokatiimi, and the only person in the game who is in
 *  the way on purpose. He stands across the porstua with the same cap on as
 *  the cook, `Mäysä` across his chest, and one arm out down the facade at the
 *  pan. Nobody gets past him hungry.
 *
 *  ------------------------------------------------------------------------
 *   WHY THIS ONE IS A COLLIDER WHEN NOBODY ELSE IS
 *  ------------------------------------------------------------------------
 *  Every other person in the venue is dressing - the sleepers are furniture,
 *  the sitter, the sprawler, the orga and the cook have no box under them at
 *  all, and that is what keeps the whole cast outside the fingerprint. This
 *  one is the exception, and he is the exception for the only reason that has
 *  ever been good enough here: he decides where the player can go.
 *
 *  So he is authored the way the veräjä is - a named solid that `Aamupala`
 *  switches off - rather than as a stander who happens to be standing
 *  somewhere awkward. The box is hashed, it is measured by `Level.test.ts`
 *  against the doorway it fills, and adding him retired the leaderboard.
 *
 *  The box is the *doorway*, not the man. It is the full clear width of the
 *  porch, because what is standing there is a man with his arm across it and
 *  the answer to "can I squeeze past" has to be no rather than
 *  nearly-no-but-try-again. The figure inside it is a normal 1.86 m body stood
 *  in the middle of it with his arm across the rest; the 1.7 m of box around
 *  him is the gesture.
 *
 *  ------------------------------------------------------------------------
 *   AND WHY HE HAS NO BOX AT ALL ONCE HE MOVES
 *  ------------------------------------------------------------------------
 *  Because he steps out of the porch rather than to one side of it. The porch
 *  is 1.7 m wide and a man is half a metre of it: "aside" inside it would be a
 *  gap you could just about carry vauhti through, which is a corner nobody
 *  asked for at the end of a run. He goes out onto the polku instead, off the
 *  porch's west corner - the far side of the door from the pan - and points
 *  back in, which puts him where the sitter and the orga are, off the line,
 *  and lets him be dressing again the moment he stops being a wall.
 * ==========================================================================
 */
export const MAYSA = {
  /**
   * Filling the porstua: the full clear width of it, a metre in front of the
   * door and a metre inside the mouth.
   *
   * Not flush against the door, and not in the mouth either. Against the door
   * he would be something you only find out about after the last corner; in
   * the mouth he would be standing on top of `nukkuja-ovella`, who is already
   * across that opening and is already the reason the porch is entered with a
   * hop. Here there is room to land, see him, and be stopped.
   *
   * This is also as far towards the yard as he comes. `Level.test.ts` prices
   * that landing at three player radii of floor between the sleeper and this
   * box, and there is 1.07 m of it left; a step further out and the toll is
   * met in mid-air over a sleeping man, which is the one way it can be worse
   * than being met too late.
   */
  minX: 129.2,
  maxX: 130.9,
  minZ: -28.75,
  maxZ: -28.35,
  /**
   * Well over any jump the level can produce - see the boot-lid arithmetic in
   * `src/config/CLAUDE.md`, which prices the highest thing in the game at a
   * touch over a metre and a half. A toll you can hop is not a toll, and this
   * is the one place in the level where that has to be true of a *person*.
   */
  height: 1.86,
  /**
   * Centred in the clear porch opening. The blocking pose bends the elbow
   * further so the pointing hand stays inside the east jamb.
   */
  standX: 130.05,
  standZ: -28.55,
  /**
   * Turned a little west of straight out of the porch, because that is the way
   * the veräjä is and therefore the way Kimmo arrives.
   */
  rotationY: -0.35,
  /**
   * And once the plate is full: out on the polku at the porch's *west* corner,
   * which is the far side of the door from the pan.
   *
   * That side rather than the other one, because the last leg of the run comes
   * back along the facade from the muurikka and turns straight into this
   * doorway. The east corner is in that line, and a man with no collider is
   * still in the way there - a runner who flinches off a person loses the same
   * tenth a wall would take off him. From this corner the whole approach goes
   * past him instead of through him, and he is the last thing you run *at*
   * rather than the last thing you run round.
   *
   * In front of the porch, with enough room for the whole pointing arm. The west wall is 2.8 m of
   * barnWall standing between this corner and every approach that matters, so
   * a step south of where it ends is a man nobody sees until they are already
   * in the doorway - and a man you find after the last corner has nothing left
   * to point at. Out here he is in the open from the muurikka onwards.
   *
   * And clear of the other line, the one running the other way: the first leg
   * comes out of the paljun veräjä and past this corner on its way to the pan.
   * He is still in the porch when it does, so nothing can touch him - but
   * `Level.test.ts` measures him against every mark on the route anyway,
   * because that leg is the one a runner improvises on.
   */
  asideX: 128.5,
  asideZ: -26.0,
} as const;

/** What his arm is aimed at while he is in the way: the pan, and the plate on it. */
const MAYSA_TARGET: [number, number, number] = [MUURIKKA.x, MUURIKKA.height, MUURIKKA.z];

/**
 * And once he has moved: the doorway he was standing in, taken off the door
 * itself rather than typed out again, so that moving the door moves his arm.
 */
const NAVETAN_OVI_KESKI = { x: 130.15, y: 1.15, z: -29.84 };

export const AAMUPALA_SPEC: AamupalaSpec = {
  // The plate's own centre. Derived rather than typed, so sliding the pan along
  // the wall slides the place you have to stand with it - and so the two can
  // never end up in different places, which is the one mistake level data of
  // this shape cannot catch on its own.
  station: [MUURIKKA.x, MUURIKKA.z],
  reach: AAMUPALA.reach,
  maysa: {
    name: 'maysa',
    gate: 'maysa-ovella',
    // The man he is standing behind. Getting over `nukkuja-ovella` is the
    // whole of what "trying this door" consists of, and it is therefore the
    // moment he says why it is shut - see `MAYSA_LINE`. The box above is what
    // stops anybody; this name only decides when he speaks.
    doorstep: 'nukkuja-ovella',
    stand: [MAYSA.standX, MAYSA.standZ],
    height: 1.86,
    rotationY: MAYSA.rotationY,
    points: MAYSA_TARGET,
    // The same cap the cook has on, because they are the same two words on the
    // same morning; the shirt is the handle, exactly like Koala's.
    cap: { text: 'Ruokatiimi', color: '#141a24', background: '#e3b23c' },
    shirt: { text: 'Mäysä', color: '#f4f1e8', background: '#2d6b52' },
    aside: {
      stand: [MAYSA.asideX, MAYSA.asideZ],
      // Turned back at the pan he spent the whole run pointing at, because
      // that is the direction Kimmo comes from once the plate is full. Square
      // to the door instead - which is where he used to face - is a man who
      // spends the last seconds of the run with his back to the only person
      // he is talking to.
      rotationY: Math.atan2(MAYSA_TARGET[0] - MAYSA.asideX, MAYSA_TARGET[2] - MAYSA.asideZ),
      // And the arm goes the other way, into the doorway he was just filling.
      // Watching the runner in and pointing past his own shoulder at the door
      // is the gesture; this is the one figure in the level whose shoulders
      // and whose hand say two different things on purpose, and it only works
      // because they are both aimed at real coordinates.
      //
      // From this corner the door is round his left as well, and far less far
      // round than it used to be: he watches the yard the pan is on and the
      // doorway sits half a right angle off that shoulder rather than most of
      // the way behind it. `pointArm` picks the side off the angle rather than
      // being told, which is what keeps a raised sleeve off his own face.
      points: [NAVETAN_OVI_KESKI.x, NAVETAN_OVI_KESKI.y, NAVETAN_OVI_KESKI.z],
    },
  },
};
